// Privacy contract shared by the API and the web app (pilot slice 5; ADR-0024; compliance baseline v2 §2).
// The mahallu is the Data Fiduciary: every value here is a default the mahallu publishes as its own.
import type { Role } from './auth';

/** Roles that run privacy for a mahallu: notice, grievance officer, retention, erasure requests. */
export const PRIVACY_ROLES: readonly Role[] = ['MAHALLU_ADMIN', 'PRESIDENT', 'SECRETARY'];

/** Purposes a house consents to separately (compliance baseline §2, COMP-02). */
export const CONSENT_PURPOSES = ['REGISTER', 'VARISANKHYA', 'CERTIFICATES', 'MADRASA', 'NOTICES', 'WELFARE'] as const;
export type ConsentPurpose = (typeof CONSENT_PURPOSES)[number];

export const CONSENT_STATES = ['GIVEN', 'WITHDRAWN'] as const;
export type ConsentState = (typeof CONSENT_STATES)[number];

/** How consent reached the committee. IMPORTED is only for paper consent that predates Rabita One. */
export const CONSENT_METHODS = ['PAPER_FORM', 'IN_PERSON', 'PHONE_CALL', 'IMPORTED'] as const;
export type ConsentMethod = (typeof CONSENT_METHODS)[number];
export const RECORDABLE_CONSENT_METHODS = ['PAPER_FORM', 'IN_PERSON', 'PHONE_CALL'] as const satisfies readonly ConsentMethod[];

/** Consent is given by an adult member of the house (the head, normally). */
export const CONSENT_MIN_AGE = 18;

export const REQUEST_CHANNELS = ['IN_PERSON', 'PHONE_CALL', 'LETTER', 'EMAIL', 'WHATSAPP'] as const;
export type RequestChannel = (typeof REQUEST_CHANNELS)[number];

export const REQUEST_STATUSES = ['RECEIVED', 'COMPLETED', 'REJECTED'] as const;
export type RequestStatus = (typeof REQUEST_STATUSES)[number];

/**
 * Days the mahallu gives itself to answer a rights request. A product default, shown in the notice;
 * counsel confirms it against the DPDP Rules (compliance baseline §12).
 */
export const RIGHTS_RESPONSE_DAYS = 30;

export const PRIVACY_LIMITS = { notice: 20000, note: 1000, officerName: 120 } as const;

// ── Retention schedule (COMP-04) ──

export type RetentionFrom = 'MEMBERSHIP_ENDS' | 'CREATED' | 'CLOSED';
export type RetentionAction = 'KEEP' | 'ERASE';

export interface RetentionDefault {
  record: string;
  labelEn: string;
  labelMl: string;
  /** null: kept for as long as the mahallu exists (records the fiduciary must keep). */
  months: number | null;
  /** The shortest period a mahallu may choose. Equal to months when it may not choose. */
  minMonths: number | null;
  from: RetentionFrom;
  action: RetentionAction;
  basisEn: string;
  basisMl: string;
}

/**
 * The default schedule. Counsel signs it off before go-live (compliance baseline §12). A mahallu may
 * lengthen any period that has one, and shorten it down to minMonths; permanent records stay permanent.
 */
export const RETENTION_SCHEDULE: readonly RetentionDefault[] = [
  {
    record: 'MEMBER_REGISTER', labelEn: 'Register entry (name, house, relation, year of birth, status)', labelMl: 'രജിസ്റ്ററിലെ വിവരങ്ങൾ (പേര്, വീട്, ബന്ധം, ജനന വർഷം, നില)',
    months: null, minMonths: null, from: 'CREATED', action: 'KEEP',
    basisEn: 'The mahallu keeps its register of members permanently; people are never deleted, their status changes.',
    basisMl: 'മഹല്ല് അംഗങ്ങളുടെ രജിസ്റ്റർ സ്ഥിരമായി സൂക്ഷിക്കുന്നു; ആരെയും നീക്കം ചെയ്യില്ല, നില മാറ്റുന്നു.',
  },
  {
    record: 'RECEIPTS', labelEn: 'Receipts and accounts', labelMl: 'രസീതുകളും കണക്കുകളും',
    months: null, minMonths: null, from: 'CREATED', action: 'KEEP',
    basisEn: 'Accounts the mahallu must keep and show (Waqf Board and audit duties).',
    basisMl: 'മഹല്ല് സൂക്ഷിക്കുകയും ഹാജരാക്കുകയും ചെയ്യേണ്ട കണക്കുകൾ (വഖഫ് ബോർഡ്, ഓഡിറ്റ്).',
  },
  {
    record: 'CERTIFICATES', labelEn: 'Certificates issued', labelMl: 'നൽകിയ സർട്ടിഫിക്കറ്റുകൾ',
    months: null, minMonths: null, from: 'CREATED', action: 'KEEP',
    basisEn: 'Each certificate must stay verifiable for as long as it may be shown.',
    basisMl: 'നൽകിയ ഓരോ സർട്ടിഫിക്കറ്റും എപ്പോഴും പരിശോധിക്കാൻ കഴിയണം.',
  },
  {
    record: 'CONTACT_DETAILS', labelEn: 'Contact details (mobile, email, place and type of work)', labelMl: 'ബന്ധപ്പെടാനുള്ള വിവരങ്ങൾ (മൊബൈൽ, ഇമെയിൽ, ജോലിസ്ഥലം, ജോലി)',
    months: 24, minMonths: 1, from: 'MEMBERSHIP_ENDS', action: 'ERASE',
    basisEn: 'Needed only to reach the family; erased earlier on request.',
    basisMl: 'കുടുംബത്തെ ബന്ധപ്പെടാൻ മാത്രം; ആവശ്യപ്പെട്ടാൽ നേരത്തെ മായ്ക്കും.',
  },
  {
    record: 'CONSENT_RECORDS', labelEn: 'Consent records', labelMl: 'സമ്മത രേഖകൾ',
    months: 84, minMonths: 36, from: 'MEMBERSHIP_ENDS', action: 'ERASE',
    basisEn: 'Proof of what was consented to, kept in case it is questioned.',
    basisMl: 'നൽകിയ സമ്മതത്തിന്റെ തെളിവ്, ചോദ്യം ചെയ്യപ്പെട്ടാൽ കാണിക്കാൻ.',
  },
  {
    record: 'PRIVACY_REQUESTS', labelEn: 'Privacy requests and their answers', labelMl: 'സ്വകാര്യതാ അപേക്ഷകളും മറുപടികളും',
    months: 36, minMonths: 12, from: 'CLOSED', action: 'ERASE',
    basisEn: 'Shows how each request was handled.',
    basisMl: 'ഓരോ അപേക്ഷയും എങ്ങനെ കൈകാര്യം ചെയ്തു എന്നതിന്റെ രേഖ.',
  },
  {
    record: 'MESSAGE_LOG', labelEn: 'Messages sent (WhatsApp, SMS, email)', labelMl: 'അയച്ച സന്ദേശങ്ങൾ (വാട്സ്ആപ്, എസ്.എം.എസ്, ഇമെയിൽ)',
    months: 12, minMonths: 6, from: 'CREATED', action: 'ERASE',
    basisEn: 'Delivery proof for receipts and notices.',
    basisMl: 'രസീതുകളും അറിയിപ്പുകളും എത്തിയതിന്റെ തെളിവ്.',
  },
  {
    record: 'SIGN_IN_RECORDS', labelEn: 'Sign-in and security logs', labelMl: 'സൈൻ-ഇൻ, സുരക്ഷാ ലോഗുകൾ',
    months: 12, minMonths: 12, from: 'CREATED', action: 'ERASE',
    basisEn: 'Security logs kept in India for at least 180 days (CERT-In Directions, 2022).',
    basisMl: 'സുരക്ഷാ ലോഗുകൾ കുറഞ്ഞത് 180 ദിവസം ഇന്ത്യയിൽ സൂക്ഷിക്കണം (CERT-In നിർദേശം, 2022).',
  },
];

export const RETENTION_RECORDS = RETENTION_SCHEDULE.map((r) => r.record);

/** Whether a mahallu may choose its own period for this record. */
export const retentionEditable = (r: RetentionDefault) => r.months !== null && r.minMonths !== null && r.minMonths < r.months;

// ── API shapes ──

export interface GrievanceOfficer {
  name: string;
  phone: string | null;
  email: string | null;
  version: number;
}

export interface OfficerInput {
  name: string;
  /** Mobile number; Indian numbers may be typed without +91. */
  phone?: string | null;
  email?: string | null;
  /** The version loaded, or omitted when no officer is named yet. */
  version?: number;
}

export interface PrivacyNotice {
  versionNo: number;
  status: 'DRAFT' | 'PUBLISHED';
  bodyEn: string;
  bodyMl: string;
  version: number;
  updatedAt: string;
  publishedAt: string | null;
}

export interface RetentionRow extends RetentionDefault {
  /** The period in force: the mahallu's own choice, or the default. */
  monthsInForce: number | null;
  custom: boolean;
  editable: boolean;
  /** The mahallu's saved choice: send it back when changing the period; null when none is saved. */
  version: number | null;
}

export interface PrivacySettings {
  officer: GrievanceOfficer | null;
  published: PrivacyNotice | null;
  draft: PrivacyNotice | null;
  retention: RetentionRow[];
  mahallu: { nameEn: string; nameMl: string | null };
  /** Address of the public notice page for this mahallu (relative). */
  publicPath: string;
}

export interface PublicPrivacy {
  mahallu: { slug: string; nameEn: string; nameMl: string | null };
  notice: { versionNo: number; bodyEn: string; bodyMl: string; publishedAt: string } | null;
  officer: { name: string; phone: string | null; email: string | null } | null;
}

export interface ConsentCurrent {
  purpose: ConsentPurpose;
  state: ConsentState | 'NONE';
  method: ConsentMethod | null;
  noticeVersion: number | null;
  givenBy: string | null;
  recordedAt: string | null;
}

export interface ConsentEvent {
  id: string;
  purpose: ConsentPurpose;
  state: ConsentState;
  method: ConsentMethod;
  noticeVersion: number | null;
  givenBy: string | null;
  note: string | null;
  recordedAt: string;
}

export interface HouseConsents {
  /** The notice version consent is recorded against now; null until one is published. */
  currentNotice: number | null;
  purposes: ConsentCurrent[];
  history: ConsentEvent[];
}

export interface ConsentInput {
  purposes: ConsentPurpose[];
  state: ConsentState;
  method: (typeof RECORDABLE_CONSENT_METHODS)[number];
  givenByMemberId: string;
  note?: string | null;
}

/** What an erasure did: field keys erased, and what stayed with the reason (a RETENTION record). */
export interface ErasureOutcome {
  erased: string[];
  kept: { record: string; basisEn: string }[];
}

export interface PrivacyRequest {
  id: string;
  requestNo: string;
  kind: 'ERASURE';
  status: RequestStatus;
  member: { id: string; nameEn: string; nameMl: string | null; houseId: string; houseNo: string; houseName: string };
  receivedVia: RequestChannel;
  requestNote: string | null;
  dueOn: string;
  overdue: boolean;
  decisionNote: string | null;
  outcome: ErasureOutcome | null;
  createdAt: string;
  closedAt: string | null;
  version: number;
}

export interface PrivacyRequestInput {
  memberId: string;
  receivedVia: RequestChannel;
  note?: string | null;
}

// ── Notice template (COMP-14 draft) ──

const PURPOSE_TEXT: Record<ConsentPurpose, { en: string; ml: string }> = {
  REGISTER: { en: 'Register: keeping the mahallu register of houses and members.', ml: 'രജിസ്റ്റർ: മഹല്ലിലെ വീടുകളുടെയും അംഗങ്ങളുടെയും രജിസ്റ്റർ സൂക്ഷിക്കൽ.' },
  VARISANKHYA: { en: 'Varisankhya: recording contributions and issuing receipts.', ml: 'വരിസംഖ്യ: വരിസംഖ്യ രേഖപ്പെടുത്തലും രസീത് നൽകലും.' },
  CERTIFICATES: { en: 'Certificates: issuing membership, nikah and death or burial certificates.', ml: 'സർട്ടിഫിക്കറ്റുകൾ: അംഗത്വ, നികാഹ്, മരണ-ഖബറടക്ക സർട്ടിഫിക്കറ്റുകൾ നൽകൽ.' },
  MADRASA: { en: 'Madrasa: admission and records of children, with a parent’s consent.', ml: 'മദ്രസ: കുട്ടികളുടെ മദ്രസ പ്രവേശനവും രേഖകളും, രക്ഷിതാവിന്റെ സമ്മതത്തോടെ.' },
  NOTICES: { en: 'Notices: announcements and reminders by WhatsApp, SMS or email.', ml: 'അറിയിപ്പുകൾ: വാട്സ്ആപ്, എസ്.എം.എസ്, ഇമെയിൽ വഴി അറിയിപ്പുകളും ഓർമപ്പെടുത്തലുകളും.' },
  WELFARE: { en: 'Welfare: welfare support, seen only by the committee members named for it.', ml: 'ക്ഷേമം: ക്ഷേമ സഹായങ്ങൾ; ഇതിനായി നിശ്ചയിച്ച കമ്മിറ്റി അംഗങ്ങൾ മാത്രം കാണും.' },
};

const FROM_TEXT: Record<RetentionFrom, { en: string; ml: string }> = {
  MEMBERSHIP_ENDS: { en: 'after membership ends', ml: 'അംഗത്വം അവസാനിച്ച ശേഷം' },
  CREATED: { en: 'after it is recorded', ml: 'രേഖപ്പെടുത്തിയ ശേഷം' },
  CLOSED: { en: 'after the request is closed', ml: 'അപേക്ഷ തീർപ്പാക്കിയ ശേഷം' },
};

/** "kept permanently" or "24 months after membership ends", in either language. */
export function retentionPeriodText(r: { months: number | null; from: RetentionFrom }, lang: 'en' | 'ml'): string {
  if (r.months === null) return lang === 'ml' ? 'സ്ഥിരമായി സൂക്ഷിക്കും' : 'kept permanently';
  return lang === 'ml' ? `${FROM_TEXT[r.from].ml} ${r.months} മാസം വരെ` : `${r.months} months ${FROM_TEXT[r.from].en}`;
}

export interface NoticeTemplateInput {
  mahalluEn: string;
  mahalluMl: string | null;
  officer: { name: string; phone: string | null; email: string | null } | null;
  retention: { labelEn: string; labelMl: string; monthsInForce: number | null; from: RetentionFrom }[];
}

/**
 * A starting draft of the mahallu's privacy notice in plain text. It is the mahallu's document: the
 * committee edits it, counsel reviews it (COMP-14), and a native reader checks the Malayalam.
 */
export function privacyNoticeTemplate(lang: 'en' | 'ml', p: NoticeTemplateInput): string {
  const officer = p.officer ? [p.officer.name, p.officer.phone, p.officer.email].filter(Boolean).join(' · ') : '—';
  const keep = p.retention.map((r) => `- ${lang === 'ml' ? r.labelMl : r.labelEn}: ${retentionPeriodText({ months: r.monthsInForce, from: r.from }, lang)}.`).join('\n');
  const purposes = CONSENT_PURPOSES.map((k) => `- ${PURPOSE_TEXT[k][lang]}`).join('\n');
  if (lang === 'ml') {
    const name = p.mahalluMl ?? p.mahalluEn;
    return `സ്വകാര്യതാ അറിയിപ്പ് — ${name}

${name} (“മഹല്ല്”, “ഞങ്ങൾ”) ഈ മഹല്ലിലെ വീടുകളുടെയും കുടുംബങ്ങളുടെയും രജിസ്റ്റർ സൂക്ഷിക്കുന്നു. ഞങ്ങൾ എന്ത് രേഖപ്പെടുത്തുന്നു, എന്തിന്, എത്ര കാലം സൂക്ഷിക്കുന്നു, നിങ്ങളുടെ അവകാശങ്ങൾ എന്തൊക്കെ എന്ന് ഈ അറിയിപ്പ് വിശദീകരിക്കുന്നു. ഈ രേഖകൾ സൂക്ഷിക്കാൻ OptiFi Technologies LLP നടത്തുന്ന Rabita One (Mahallu Cloud by OptiFi) എന്ന സേവനം ഞങ്ങൾ ഉപയോഗിക്കുന്നു. ഞങ്ങളുടെ നിർദേശപ്രകാരം മാത്രമാണ് OptiFi ഇവ കൈകാര്യം ചെയ്യുന്നത്; വിവരങ്ങൾ ഇന്ത്യയിൽ തന്നെ സൂക്ഷിക്കുന്നു.

ഞങ്ങൾ രേഖപ്പെടുത്തുന്നത്
- വീട്: വീട്ടുപേരും നമ്പറും, വാർഡ്, ഉടമസ്ഥത, വരിസംഖ്യ പ്ലാൻ.
- കുടുംബാംഗങ്ങൾ: ഇംഗ്ലീഷിലും മലയാളത്തിലുമുള്ള പേര്, ഗൃഹനാഥനുമായുള്ള ബന്ധം, ജനന വർഷം, ലിംഗം, നാട്ടിലാണോ ഇന്ത്യയിൽ മറ്റിടത്താണോ വിദേശത്താണോ താമസം എന്നതും സ്ഥലവും.
- ബന്ധപ്പെടാനുള്ള വിവരങ്ങൾ: മൊബൈൽ നമ്പർ, ഇമെയിൽ, പുറത്ത് താമസിക്കുന്നവരുടെ ജോലിസ്ഥലവും ജോലിയും.
- ലഭിച്ച തുകകൾ, നൽകിയ സർട്ടിഫിക്കറ്റുകൾ, ലഭിച്ച അപേക്ഷകൾ എന്നിവയുടെ രേഖകൾ.
ഒരു സർക്കാർ തിരിച്ചറിയൽ നമ്പറും ഞങ്ങൾ രേഖപ്പെടുത്തുന്നില്ല.

ഉപയോഗിക്കുന്ന ആവശ്യങ്ങൾ (ഓരോന്നിനും ഗൃഹനാഥന്റെയോ പ്രായപൂർത്തിയായ മറ്റൊരു അംഗത്തിന്റെയോ സമ്മതം വേണം)
${purposes}
ഏത് ആവശ്യത്തിനുള്ള സമ്മതവും, നൽകിയത്ര എളുപ്പത്തിൽ, എപ്പോൾ വേണമെങ്കിലും സെക്രട്ടറിയെയോ പരാതി പരിഹാര ഓഫീസറെയോ അറിയിച്ച് പിൻവലിക്കാം. അതിനു മുമ്പ് ചെയ്തതിനെ പിൻവലിക്കൽ ബാധിക്കില്ല. വരിസംഖ്യ കുടിശ്ശികയുടെ പേരിൽ ഒരു സേവനവും നിഷേധിക്കില്ല.

എത്ര കാലം സൂക്ഷിക്കും
${keep}

നിങ്ങളുടെ അവകാശങ്ങൾ
നിങ്ങളെക്കുറിച്ച് ഞങ്ങൾ സൂക്ഷിക്കുന്ന വിവരങ്ങൾ കാണാനും തിരുത്താനും മായ്ക്കാനും ആവശ്യപ്പെടാം. മഹല്ലിന്റെ ചുമതലകളുടെ ഭാഗമായി ചില രേഖകൾ (രജിസ്റ്ററിലെ വിവരങ്ങൾ, രസീതുകൾ, സർട്ടിഫിക്കറ്റുകൾ) സൂക്ഷിക്കേണ്ടതുണ്ട്; എന്ത് സൂക്ഷിക്കുന്നു, എന്തുകൊണ്ട് എന്ന് ഞങ്ങൾ അറിയിക്കും. ${RIGHTS_RESPONSE_DAYS} ദിവസത്തിനകം മറുപടി നൽകും.

പരാതി പരിഹാര ഓഫീസർ
${officer}
ഞങ്ങളുടെ മറുപടിയിൽ തൃപ്തരല്ലെങ്കിൽ ഡാറ്റാ പ്രൊട്ടക്ഷൻ ബോർഡ് ഓഫ് ഇന്ത്യയ്ക്ക് പരാതി നൽകാം.`;
  }
  const name = p.mahalluEn;
  return `Privacy notice — ${name}

${name} (“the mahallu”, “we”) keeps a register of the houses and families of this mahallu. This notice explains what we record, why, how long we keep it, and your rights. We keep these records in Rabita One (Mahallu Cloud by OptiFi), a service run by OptiFi Technologies LLP. OptiFi handles them only on our instructions, and the information stays in India.

What we record
- House: house name and number, ward, ownership, varisankhya plan.
- Family members: names in English and Malayalam, relation to the head of the house, year of birth, gender, and whether they live here, elsewhere in India or abroad, and where.
- Contact details: mobile number, email, and the place and type of work of members living away.
- Records of money received, certificates issued and requests made.
We do not record any government ID number.

What we use it for (each purpose needs consent from the head of the house or another adult member)
${purposes}
You can withdraw consent for any purpose at any time, as easily as you gave it, by telling the secretary or the grievance officer. Withdrawing does not undo what was done before. No service is refused because varisankhya is unpaid.

How long we keep it
${keep}

Your rights
You can ask to see the information we hold about you, to correct it, or to erase it. Some records must be kept as part of the mahallu’s duties (the register entry, receipts and certificates); we will tell you what we keep and why. We answer within ${RIGHTS_RESPONSE_DAYS} days.

Grievance officer
${officer}
If you are not satisfied with our answer, you may complain to the Data Protection Board of India.`;
}
